import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { PublisherInfoDialog } from '@/components/publisher/PublisherInfoDialog';

const opportunityTypes = [
  { value: 'emploi', label: 'Emploi' },
  { value: 'stage', label: 'Stage' },
  { value: 'formation', label: 'Formation' },
  { value: 'bourse', label: 'Bourse' },
  { value: 'volontariat', label: 'Volontariat' },
] as const;

// Base schema
const baseSchema = z.object({
  type: z.enum(['emploi', 'stage', 'formation', 'bourse', 'volontariat']),
  title: z.string().min(3, 'Le titre doit contenir au moins 3 caractères'),
  company: z.string().min(2, 'Le nom de l\'organisme est requis'),
  location: z.string().min(2, 'Le lieu est requis'),
  arrondissement: z.string().optional(),
  description: z.string().min(50, 'La description doit contenir au moins 50 caractères'),
  contact_email: z.string().email('Email invalide').optional().or(z.literal('')),
  contact_phone: z.string().optional(),
  application_url: z.string().url('URL invalide').optional().or(z.literal('')),
});

const PublishOpportunity = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [showPublisherDialog, setShowPublisherDialog] = useState(true);
  const [publisherInfo, setPublisherInfo] = useState<{
    id: string;
    full_name: string;
    email: string;
    phone: string;
  } | null>(null);

  const form = useForm<any>({
    defaultValues: {
      type: undefined,
      title: '',
      company: '',
      location: '',
      arrondissement: '',
      description: '',
      contact_email: '',
      contact_phone: '',
      application_url: '',
      salary: '',
      deadline: undefined,
      responsibilities: [],
      requirements: [],
      internship_duration: '',
      is_paid: false,
      training_duration: '',
      training_cost: '',
      is_online: false,
      scholarship_amount: '',
      eligibility_criteria: '',
      volunteer_duration: '',
      contribution: '',
      mission_description: '',
    },
  });

  const handlePublisherSaved = (publisherId: string, data: any) => {
    setPublisherInfo({
      id: publisherId,
      full_name: data.full_name,
      email: data.email,
      phone: data.phone,
    });
    setShowPublisherDialog(false);
  };

  const onSubmit = async (data: any) => {
    if (!publisherInfo) {
      toast.error('Veuillez d\'abord renseigner vos informations');
      setShowPublisherDialog(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare the data for insertion
      const opportunityData: any = {
        type: data.type,
        title: data.title,
        company: data.company,
        location: data.location,
        arrondissement: data.arrondissement || null,
        description: data.description,
        contact_email: data.contact_email || null,
        contact_phone: data.contact_phone || null,
        application_url: data.application_url || null,
        publisher_id: publisherInfo.id,
        publisher_name: publisherInfo.full_name,
        publisher_email: publisherInfo.email,
        publisher_phone: publisherInfo.phone,
        created_by: null,
        status: 'pending',
      };

      // Add type-specific fields
      if (data.type === 'emploi') {
        opportunityData.salary = data.salary || null;
        opportunityData.deadline = data.deadline?.toISOString();
        opportunityData.responsibilities = data.responsibilities || null;
        opportunityData.requirements = data.requirements || null;
      } else if (data.type === 'stage') {
        opportunityData.internship_duration = data.internship_duration;
        opportunityData.is_paid = data.is_paid;
        opportunityData.deadline = data.deadline?.toISOString();
        opportunityData.requirements = data.requirements || null;
      } else if (data.type === 'formation') {
        opportunityData.training_duration = data.training_duration;
        opportunityData.training_cost = data.training_cost || null;
        opportunityData.is_online = data.is_online;
        opportunityData.deadline = data.deadline?.toISOString() || null;
        opportunityData.requirements = data.requirements || null;
      } else if (data.type === 'bourse') {
        opportunityData.scholarship_amount = data.scholarship_amount;
        opportunityData.eligibility_criteria = data.eligibility_criteria;
        opportunityData.deadline = data.deadline?.toISOString();
      } else if (data.type === 'volontariat') {
        opportunityData.volunteer_duration = data.volunteer_duration;
        opportunityData.contribution = data.contribution || null;
        opportunityData.mission_description = data.mission_description;
        opportunityData.deadline = data.deadline?.toISOString() || null;
      }

      const { error } = await supabase.from('opportunities').insert([opportunityData]);

      if (error) throw error;

      toast.success('Votre offre a été soumise avec succès ! Elle sera publiée après validation par un administrateur.');
      navigate('/opportunities');
    } catch (error: any) {
      console.error('Error creating opportunity:', error);
      toast.error('Une erreur est survenue lors de la création de l\'offre');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PublisherInfoDialog
        open={showPublisherDialog}
        onOpenChange={setShowPublisherDialog}
        onPublisherSaved={handlePublisherSaved}
      />
      
      {publisherInfo && (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-primary/5 to-background relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
            <div className="absolute top-40 left-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-40 right-20 w-[500px] h-[500px] bg-primary/15 rounded-full blur-3xl animate-pulse animation-delay-200"></div>
          </div>
          
          <Header />
          <main className="flex-1 container mx-auto px-4 py-12 relative z-10">
            <Card className="max-w-3xl mx-auto shadow-xl border-2 animate-fade-in">
              <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-transparent">
                <CardTitle className="text-4xl font-poppins text-foreground">Publier une offre</CardTitle>
                <CardDescription className="text-base">
                  Remplissez le formulaire ci-dessous pour soumettre votre offre. Elle sera publiée après validation par notre équipe.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type d'offre *</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedType(value);
                            }}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionnez un type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {opportunityTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {selectedType && (
                      <>
                        <FormField
                          control={form.control}
                          name="title"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {selectedType === 'emploi' ? 'Titre du poste' : 
                                 selectedType === 'stage' ? 'Intitulé du stage' :
                                 selectedType === 'formation' ? 'Nom de la formation' :
                                 selectedType === 'bourse' ? 'Nom de la bourse' :
                                 'Nom du programme'} *
                              </FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {selectedType === 'emploi' ? 'Nom de l\'entreprise' : 
                                 selectedType === 'volontariat' ? 'Organisation hôte' :
                                 'Organisme'} *
                              </FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="location"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Lieu *</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Ex: Brazzaville" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="arrondissement"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Arrondissement</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Ex: Poto-Poto" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Type-specific fields */}
                        {selectedType === 'emploi' && (
                          <>
                            <FormField
                              control={form.control}
                              name="salary"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Salaire</FormLabel>
                                  <FormControl>
                                    <Input {...field} placeholder="Ex: 300 000 - 500 000 FCFA/mois" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="deadline"
                              render={({ field }) => (
                                <FormItem className="flex flex-col">
                                  <FormLabel>Date limite de candidature *</FormLabel>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <FormControl>
                                        <Button
                                          variant="outline"
                                          className={cn(
                                            "w-full pl-3 text-left font-normal",
                                            !field.value && "text-muted-foreground"
                                          )}
                                        >
                                          {field.value ? (
                                            format(field.value, "PPP", { locale: fr })
                                          ) : (
                                            <span>Choisir une date</span>
                                          )}
                                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                        </Button>
                                      </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <Calendar
                                        mode="single"
                                        selected={field.value}
                                        onSelect={field.onChange}
                                        disabled={(date) =>
                                          date < new Date()
                                        }
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </>
                        )}

                        {selectedType === 'stage' && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="internship_duration"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Durée *</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Ex: 3 mois" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="is_paid"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                      <FormLabel>Stage rémunéré</FormLabel>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={form.control}
                              name="deadline"
                              render={({ field }) => (
                                <FormItem className="flex flex-col">
                                  <FormLabel>Date limite de candidature *</FormLabel>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <FormControl>
                                        <Button
                                          variant="outline"
                                          className={cn(
                                            "w-full pl-3 text-left font-normal",
                                            !field.value && "text-muted-foreground"
                                          )}
                                        >
                                          {field.value ? (
                                            format(field.value, "PPP", { locale: fr })
                                          ) : (
                                            <span>Choisir une date</span>
                                          )}
                                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                        </Button>
                                      </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <Calendar
                                        mode="single"
                                        selected={field.value}
                                        onSelect={field.onChange}
                                        disabled={(date) =>
                                          date < new Date()
                                        }
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </>
                        )}

                        {selectedType === 'formation' && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="training_duration"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Durée *</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Ex: 6 mois" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="training_cost"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Coût</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Ex: 150 000 FCFA" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={form.control}
                              name="is_online"
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value}
                                      onCheckedChange={field.onChange}
                                    />
                                  </FormControl>
                                  <div className="space-y-1 leading-none">
                                    <FormLabel>Formation en ligne</FormLabel>
                                  </div>
                                </FormItem>
                              )}
                            />
                          </>
                        )}

                        {selectedType === 'bourse' && (
                          <>
                            <FormField
                              control={form.control}
                              name="scholarship_amount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Montant ou avantages *</FormLabel>
                                  <FormControl>
                                    <Input {...field} placeholder="Ex: 500 000 FCFA/an" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="eligibility_criteria"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Critères d'éligibilité *</FormLabel>
                                  <FormControl>
                                    <Textarea {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="deadline"
                              render={({ field }) => (
                                <FormItem className="flex flex-col">
                                  <FormLabel>Date limite de candidature *</FormLabel>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <FormControl>
                                        <Button
                                          variant="outline"
                                          className={cn(
                                            "w-full pl-3 text-left font-normal",
                                            !field.value && "text-muted-foreground"
                                          )}
                                        >
                                          {field.value ? (
                                            format(field.value, "PPP", { locale: fr })
                                          ) : (
                                            <span>Choisir une date</span>
                                          )}
                                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                        </Button>
                                      </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <Calendar
                                        mode="single"
                                        selected={field.value}
                                        onSelect={field.onChange}
                                        disabled={(date) =>
                                          date < new Date()
                                        }
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </>
                        )}

                        {selectedType === 'volontariat' && (
                          <>
                            <FormField
                              control={form.control}
                              name="volunteer_duration"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Durée *</FormLabel>
                                  <FormControl>
                                    <Input {...field} placeholder="Ex: 12 mois" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="contribution"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Contribution offerte</FormLabel>
                                  <FormControl>
                                    <Input {...field} placeholder="Ex: Hébergement et repas" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="mission_description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Description de la mission *</FormLabel>
                                  <FormControl>
                                    <Textarea {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </>
                        )}

                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Description *</FormLabel>
                              <FormControl>
                                <Textarea {...field} rows={6} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold">Informations de contact</h3>
                          
                          <FormField
                            control={form.control}
                            name="contact_email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email de contact</FormLabel>
                                <FormControl>
                                  <Input {...field} type="email" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="contact_phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Téléphone de contact</FormLabel>
                                <FormControl>
                                  <Input {...field} type="tel" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="application_url"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Lien de candidature</FormLabel>
                                <FormControl>
                                  <Input {...field} type="url" placeholder="https://..." />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <Button type="submit" className="w-full" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Publication en cours...
                            </>
                          ) : (
                            'Publier l\'offre'
                          )}
                        </Button>
                      </>
                    )}
                  </form>
                </Form>
              </CardContent>
            </Card>
          </main>
          <Footer />
        </div>
      )}
    </>
  );
};

export default PublishOpportunity;
